import { useDispatch, useSelector } from "react-redux";
// import SearchBar from "../../../../../Common/SearchComponent";
import FormCard from "../../../../../Lib/FormCard";
import FormInput from "../../../../../Lib/FormInput";
import FormSelect from "../../../../../Lib/FormSelect";
import { guestSearchRequest } from "../../../../../Redux/Actions";
import { debounce } from "lodash";
import React, { useEffect, useState } from "react";

const GuestDetailForm = ({
  guestIndex,
  guestData,
  setGuestData,
  isViewOnly = false,
  onRemoveGuest,
}) => {
  const dispatch = useDispatch();
  const { guestSearch } = useSelector((state) => state.guestDetails);

  const [searchResults, setSearchResults] = useState([]);
  const [isManualEdit, setIsManualEdit] = useState(false);
  const [showSearchResults, setShowSearchResults] = useState(false);
  const [errors, setErrors] = useState({});

  // Debounced search effect
  useEffect(() => {
    if (!isManualEdit || isViewOnly) return;

    const { first_name, email, phone } = guestData;
    const delayedSearch = debounce(() => {
      dispatch(guestSearchRequest({ name: first_name, email, phone }));
    }, 500);

    if (first_name || email || phone) {
      delayedSearch();
    }

    return () => delayedSearch.cancel?.();
  }, [
    guestData.first_name,
    guestData.email,
    guestData.phone,
    dispatch,
    isManualEdit,
    isViewOnly,
  ]);

  useEffect(() => {
    const guests = guestSearch?.guests || [];
    if (guests.length > 0 && isManualEdit && !isViewOnly) {
      setSearchResults(guests);
      setShowSearchResults(true);
    } else {
      setShowSearchResults(false);
    }
  }, [guestSearch, isManualEdit, isViewOnly]);

  // Phone number validation function
  const validatePhone = (phone) => {
    if (!phone) return ""; // Allow empty phone numbers

    // Remove all non-digit characters
    const cleanPhone = phone.replace(/\D/g, "");

    // Check if it's a valid phone number (7-15 digits)
    if (cleanPhone.length < 7 || cleanPhone.length > 15) {
      return "Phone number must be between 7 and 15 digits";
    }

    // Check if it contains only digits
    if (!/^\d+$/.test(cleanPhone)) {
      return "Phone number must contain only digits";
    }

    return ""; // No error
  };

  const handleInputChange = (e) => {
    if (isViewOnly) return;

    const { name, value } = e.target;
    const updatedGuest = { ...guestData, [name]: value };
    setGuestData(guestIndex, updatedGuest);
    setIsManualEdit(true);

    // Validate phone number if it's the phone field
    if (name === "phone") {
      const phoneError = validatePhone(value);
      setErrors((prev) => ({
        ...prev,
        phone: phoneError,
      }));
    } else {
      // Clear phone error if it's not the phone field
      setErrors((prev) => ({
        ...prev,
        phone: "",
      }));
    }
  };

  const handleGuestSelect = (guest) => {
    if (isViewOnly) return;

    // Create a new guest object with only the relevant fields
    const selectedGuest = {
      first_name: guest.first_name || "",
      last_name: guest.last_name || "",
      email: guest.email || "",
      phone: guest.phone || "",
      country_code: guest.country_code || "",
      father_name: guest.father_name || "",
      gender: guest.gender || "",
      occupation: guest.occupation || "",
      date_of_birth: guest.date_of_birth || "",
      nationality: guest.nationality || "",
      country: guest.guest_details?.country || "",
      guest_details: {
        country: guest.guest_details?.country || "",
        state: guest.guest_details?.state || "",
        city: guest.guest_details?.city || "",
        zip_code: guest.guest_details?.zip_code || "",
        address: guest.guest_details?.address || "",
        comment: guest.guest_details?.comment || "",
        document_type: guest.guest_details?.document_type || "",
        guestDocuments: guest.guest_details?.guestDocuments || null,
        guestDocumentUrl: guest.guest_details?.guestDocumentUrl || "",
        frontend_url: guest.guest_details?.frontend_url || "",
        country_code: guest.country_code || "",
      },
    };

    // Update only the selected guest
    setGuestData(guestIndex, selectedGuest);
    setShowSearchResults(false);
    setIsManualEdit(false);

    // Clear any validation errors when guest is selected
    setErrors({});
  };

  const fields = [
    { label: "First Name", name: "first_name", type: "text" },
    { label: "Last Name", name: "last_name", type: "text" },
    { label: "Email", name: "email", type: "email" },
    { label: "Phone", name: "phone", type: "text" },
    { label: "Father's Name", name: "father_name", type: "text" },
    {
      label: "Gender",
      name: "gender",
      type: "select",
      options: [
        { label: "Male", value: "male" },
        { label: "Female", value: "female" },
      ],
    },
    { label: "Occupation", name: "occupation", type: "text" },
    { label: "Date of Birth", name: "date_of_birth", type: "date" },
    { label: "Nationality", name: "nationality", type: "text" },
  ];

  return (
    <FormCard
      title={`Guest ${guestIndex + 1}`}
      headerAction={
        !isViewOnly &&
        onRemoveGuest && (
          <button
            type="button"
            className="btn btn-sm btn-outline-danger"
            onClick={() => onRemoveGuest(guestIndex)}
          >
            Remove Guest
          </button>
        )
      }
    >
      <div className="row">
        {fields.map((field) => (
          <div className="col-md-6" key={field.name}>
            {isViewOnly ? (
              <div className="view-only-field mb-3">
                <label className="form-label">{field.label}</label>
                <div className="view-only-value">
                  {guestData[field.name] || "N/A"}
                </div>
              </div>
            ) : field.type === "select" ? (
              <FormSelect
                label={field.label}
                name={field.name}
                options={field.options}
                value={guestData[field.name] || ""}
                onChange={handleInputChange}
                placeholder={`Choose ${field.label}`}
              />
            ) : (
              <FormInput
                label={field.label}
                name={field.name}
                type={field.type}
                placeholder={field.label}
                value={guestData[field.name] || ""}
                onChange={handleInputChange}
                error={field.name === "phone" ? errors.phone : ""}
              />
            )}
          </div>
        ))}
      </div>

      {!isViewOnly && showSearchResults && (
        <div className="row mt-2">
          <div className="col-12">
            <div className="search-suggestions border rounded p-2 bg-light">
              <strong>Suggestions:</strong>
              {searchResults.map((guest, i) => (
                <div
                  key={i}
                  onClick={() => handleGuestSelect(guest)}
                  className="suggestion-item p-1"
                  style={{ cursor: "pointer" }}
                >
                  {guest.first_name} {guest.last_name} ({guest.email})
                </div>
              ))}
            </div>
          </div>
        </div>
      )}
    </FormCard>
  );
};

export default GuestDetailForm;
